import type { HybrisOrder } from '../../models/hybris/hybrisOrder';
import type { HybrisOrderEditData } from '../../models/hybris/hybrisOrderEditData';
import type {
    ApiCompleteEditOrderRequest,
    ApiCreateOrderRequest,
    ApiGetOrderByIdRequest,
    ApiGetOrderHistoryRequest,
    ApiOrderByIdRequest,
    ApiOrderUserRequest,
} from './requests/orderDirectApiRequests';
import type {
    HybrisCancelOrderResponse,
    HybrisOrderHistoryResponse,
} from './responses/orderDirectApiResponses';

import { appInsights } from '../../../../appInsights';
import { createDefaultException } from '../../exceptions/exceptionUtils';
import getDirectHybrisAxiosClient, {
    getUserIdUrlSegment,
} from '../clients/directHybrisAxiosClient';
import { editOrderErrorInterceptor } from './orderDirectApiInterceptors';

const X_TOTAL_COUNT_HEADER = 'x-total-count';

const ORDER_ERRORS = {
    EDIT_FAILED: 'Kan inte redigera order',
    CANCEL_EDIT_FAILED: 'Det går inte att avbryta redigeringsordern',
} as const;

const encodePathSegment = (value: string): string => encodeURIComponent(value);
const getOrdersBaseUrl = (userId: string): string =>
    `users/${encodePathSegment(getUserIdUrlSegment(userId))}/orders`;

export const orderDirectApiUrls = {
    getOrderById: (request: ApiOrderByIdRequest) =>
        `${getOrdersBaseUrl(request.userId)}/${encodePathSegment(request.orderId)}`,
    getOrderHistory: (request: ApiOrderUserRequest) => getOrdersBaseUrl(request.userId),
    cancelOrder: (request: ApiOrderByIdRequest) =>
        `${getOrdersBaseUrl(request.userId)}/${encodePathSegment(request.orderId)}/cancel`,
    getOrderCount: (request: ApiOrderUserRequest) => getOrdersBaseUrl(request.userId),
    createOrder: (request: ApiCreateOrderRequest) => `${getOrdersBaseUrl(request.userId)}/form`,
    completeEditOrder: (request: ApiOrderUserRequest) =>
        `${getOrdersBaseUrl(request.userId)}/edited`,
    editOrder: (request: ApiOrderByIdRequest) =>
        `${getOrdersBaseUrl(request.userId)}/${encodePathSegment(request.orderId)}/edited`,
    cancelEditOrder: (request: ApiOrderByIdRequest) =>
        `${getOrdersBaseUrl(request.userId)}/${encodePathSegment(request.orderId)}/edited`,
} satisfies {
    getOrderById: (request: ApiOrderByIdRequest) => string;
    getOrderHistory: (request: ApiOrderUserRequest) => string;
    cancelOrder: (request: ApiOrderByIdRequest) => string;
    getOrderCount: (request: ApiOrderUserRequest) => string;
    createOrder: (request: ApiCreateOrderRequest) => string;
    completeEditOrder: (request: ApiCompleteEditOrderRequest) => string;
    editOrder: (request: ApiOrderByIdRequest) => string;
    cancelEditOrder: (request: ApiOrderByIdRequest) => string;
};

const parseOrderCountHeader = (countHeader: string | undefined): number | undefined => {
    if (!countHeader) {
        return undefined;
    }

    const count = parseInt(countHeader, 10);
    return Number.isNaN(count) ? undefined : count;
};

// send as application/x-www-form-urlencoded
const buildCreateOrderFormData = (request: ApiCreateOrderRequest): URLSearchParams => {
    const formData = new URLSearchParams({
        isSubscription: request.isSubscription?.toString() ?? 'false',
        cartId: request.cartId,
    });

    if (request.isSubscription && request.frequency !== undefined) {
        formData.append('frequency', request.frequency.toString());
    }

    if (request.transaction) {
        // Remove line breaks from transaction string
        const cleanTransaction = request.transaction.replace(/\r\n?|\n/g, '');
        formData.append('transaction', cleanTransaction);
    }

    if (request.paymentMode) {
        formData.append('paymentMode', request.paymentMode);
    }

    if (request.securityCode) {
        formData.append('securityCode', request.securityCode);
    }

    return formData;
};

// Some Hybris order-edit calls return HTTP 2xx with failure in the response body (`error` field).
// Axios error interceptors only run when the request fails as HTTP (non-2xx), so they never see
// that case. We therefore check the body after a successful response instead of using an interceptor.
const rejectOnBodyError = (
    data: HybrisOrderEditData,
    place: string,
    friendlyMessage: string,
): HybrisOrderEditData => {
    if (data.error) {
        appInsights.trackException({ exception: new Error(data.error) }, { place });
        throw createDefaultException(friendlyMessage);
    }
    return data;
};

export const createOrderDirectApi = (
    getClient: typeof getDirectHybrisAxiosClient = getDirectHybrisAxiosClient,
) => {
    return {
        getOrderById: (accessToken: string) => async (request: ApiGetOrderByIdRequest) => {
            const response = await getClient(accessToken).get<HybrisOrder>(
                orderDirectApiUrls.getOrderById(request),
                request.fields ? { params: { fields: request.fields } } : undefined,
            );
            return response.data;
        },
        getOrderHistory: (accessToken: string) => async (request: ApiGetOrderHistoryRequest) => {
            const response = await getClient(accessToken).get<HybrisOrderHistoryResponse>(
                orderDirectApiUrls.getOrderHistory(request),
                {
                    params: {
                        currentPage: request.currentPage,
                        pageSize: request.pageSize,
                        fields: 'FULL',
                    },
                },
            );

            return Array.isArray(response.data?.orders) ? response.data.orders : [];
        },
        cancelOrder: (accessToken: string) => async (request: ApiOrderByIdRequest) => {
            const response = await getClient(accessToken).post<HybrisCancelOrderResponse>(
                orderDirectApiUrls.cancelOrder(request),
            );
            const resp = response?.data?.cancelOk || false;
            return resp;
        },
        getOrderCount: (accessToken: string) => async (request: ApiOrderUserRequest) => {
            const response = await getClient(accessToken).head(
                orderDirectApiUrls.getOrderCount(request),
            );
            const countString = response.headers[X_TOTAL_COUNT_HEADER] as string | undefined;
            return parseOrderCountHeader(countString);
        },
        completeEditOrder:
            (accessToken: string) => async (request: ApiCompleteEditOrderRequest) => {
                const response = await getClient(accessToken, [
                    editOrderErrorInterceptor,
                ]).post<HybrisOrder>(
                    orderDirectApiUrls.completeEditOrder(request),
                    new URLSearchParams({
                        cartId: request.cartId,
                    }),
                );
                return response.data;
            },
        editOrder: (accessToken: string) => async (request: ApiOrderByIdRequest) => {
            const response = await getClient(accessToken).put<HybrisOrderEditData>(
                orderDirectApiUrls.editOrder(request),
            );

            return rejectOnBodyError(response.data, 'editOrder', ORDER_ERRORS.EDIT_FAILED);
        },
        cancelEditOrder: (accessToken: string) => async (request: ApiOrderByIdRequest) => {
            const response = await getClient(accessToken).delete<HybrisOrderEditData>(
                orderDirectApiUrls.cancelEditOrder(request),
            );

            return rejectOnBodyError(
                response.data,
                'cancelEditOrder',
                ORDER_ERRORS.CANCEL_EDIT_FAILED,
            );
        },
        createOrder: (accessToken: string) => async (request: ApiCreateOrderRequest) => {
            const response = await getClient(accessToken).post<HybrisOrder>(
                orderDirectApiUrls.createOrder(request),
                buildCreateOrderFormData(request),
            );
            return response.data;
        },
    };
};

export const orderDirectApi = createOrderDirectApi();
